import { AppDate } from '../envTypes';

import { ModelBase } from './base';
import { BasicProfile, ProfileUserType, StoreType } from './profile';
import {
  PopStoreExtendedSubscriptionLevel,
  PopStoreUserAccessLevel,
} from './creator-subscription';
import { SaasSubscriptionBasePlanType } from './saas-subscription';
import { ContentModerationInfo } from './moderation';
import { MediaGalleryObjectTag, PopClipProductInfo } from './popclip';
import {
  SeriesChapterLabelFormat,
  SeriesElementContentType,
  SeriesElementSortingStrategy,
  SeriesLabelFormat,
} from './series';
import { ListingType, Listing } from './listing';
import { PostContentType, PostVisibility, PostMedia, Post } from './post';
import { ReportReasonEnum } from './shared';

export enum CommunityChannelVisibility {
  Public = 'public',
  Private = 'private',
}

// /stores/{storeId}/communityChannels/{autoId}
export interface CommunityChannel extends ModelBase {
  // creator uploaded icon for this channel
  icon: string;

  /** name of the channel */
  name: string;

  // a public user and url friendly identifier for this channel which is derived
  // automatically from the name. for example, if the name is "Awesome Channel",
  // the slug will be "awesome-channel" and the link to this channel will be
  // `https://pop.store/andy/community/awesome-channel`. Note channel slug should
  // be unique in the scope of the creator's account.
  slug: string;

  /** description of the channel, which is not changed frequently */
  description?: string;

  // announcement is some message creator can set to the channel, might be
  // updated frequently, and will be shown at the top of the channel
  // in markdown format.
  announcement?: string;

  /**
   * either public or private. Anyone can enter a public channel and see the
   * messages in it, but subscription level requirement must be met in order
   * to enter and join a private one.
   */
  visibility: CommunityChannelVisibility;

  /**
   * If this channel is a broadcast only channel, which means users in this
   * channel can only view messages and take reactions with emojis but
   * cannot send any messages. Creator can still send messages and replies.
   */
  isBroadcastChannel?: boolean;

  /**
   * Create specified minimum subscription level which put restricts
   * on members that either try to join if channel is public, or try to
   * enter a channel if it's private.
   */
  minimumSubscriptionLevel: PopStoreExtendedSubscriptionLevel;

  /**
   * the saas subscription base plan type with which this channel
   * was created. when the creator's active plan is canceled or below
   * this plan type, the channel will be locked to readonly mode for
   * creator and no activity is allowed for any one in the channel.
   */
  seedBasePlanType: SaasSubscriptionBasePlanType;

  /**
   * A list of members that are currently typing in the channel,
   * limit to the predefined max N (e.g., 3). moved to a sub-collection?
   * */
  membersTyping: Array<{
    userId: string;
    profile: BasicProfile;
    // if this value is older than the predefined max seconds(e.g., 5), it will be ignored
    lastUpdatedAt: AppDate;
  }>;

  // level 1 message account, lazily computed
  messageCount: number;
  memberCount: number;
  // last level 1 message
  lastMessage?: BasicCommunityChannelMessage;
}

// companion interface for the community channel typing
// /stores/{storeId}/communityChannelsTyping/{channelId}
export interface CommunityChannelTyping extends ModelBase {
  typing: string[]; // serialized BasicProfile[]
}

export enum CommunityChannelMemberStatus {
  Active = 'active', // the user is a member of the channel
  Left = 'left',
}

export enum CommunityMemberPresenceStatus {
  Online = 'online',
  Offline = 'offline',
  Away = 'away',
}

export interface CommunityChannelMemberNotificationSettings {
  muted: boolean; // mute all notifications for this channel
  // notify when creator posts a new message
  subscribedToUpdatesOfCreator: boolean;
  // subscribed to updates of specific messages by ids (top level messages)
  subscribedToUpdatesOfMessages: string[];
  // subscribe to mentions in the channel
  subscribedToMentions: boolean;
}

export interface CommunityChannelMemberProfile extends BasicProfile {
  userType?: ProfileUserType;
  storeType?: StoreType;
  storeFrontName?: string;
  urlCode?: string;
}

// /stores/{storeId}/communityChannels/{channelId}/members/{userId}
export interface CommunityChannelMember extends ModelBase {
  profile: CommunityChannelMemberProfile;
  // snapshot of the user's current subscription level
  subscriptionLevel: PopStoreExtendedSubscriptionLevel;
  // the minimum required subscription level to join this channel or chat in this channel
  // this is used to determine if the user can continue chatting in this channel
  // if the minimum required subscription level of the channel is changed to
  // to be higher than the require susbcription level when the user joined the channel.
  // it's set to the channel's minimum subscripotion level requirement initially
  // when the user join this channel for the first time.
  // when the minimum required subscription level is changed:
  // - apply to new users only: don't change this value so that the users can continue chatting
  // - apply to all users: change this value to the new minimum required subscription level
  minimumRequiredSubscriptionLevel: PopStoreExtendedSubscriptionLevel;
  lastReadAt?: AppDate;
  lastDeleteAt?: AppDate;
  status: CommunityChannelMemberStatus;
  /** member's online status */
  presenceStatus: CommunityMemberPresenceStatus;
  // used to check if online status is stale
  onlineLastUpdatedAt?: AppDate;
  notificationSettings: CommunityChannelMemberNotificationSettings;
  blocked: boolean;
  blockedAt?: AppDate;
  leftAt?: AppDate;
}

export enum CommunityChannelMessageLinkType {
  VideoClip = 'videoClip',
  Series = 'series',
  Post = 'post',
  Course = 'course',
  Product = 'product',
  // internal or external unknown links
  Unknown = 'unknown',
}

export interface CommunityChannelMessageAttachment {
  /** the name of the file */
  filename: string;
  /** the content type of the file, e.g. image/jpeg */
  contentType: string;
  /** media gallery object id for image/videos */
  objectId?: string;
  /** the url of the attachment file with embedded metadata */
  url: string;
  /** processed video url for video files */
  processedUrl?: string;
  /** thumbnail image url if available for image/video files */
  thumbnailUrl?: string;
  /** video cover/thumbnail url if available for video files */
  thumbnailVideoUrl?: string;
  /** optional caption for the attachment */
  caption?: string;
  /** the blurhash of the image/video that can be used to
   * render the placeholder */
  blurhash?: string;
  /** moderation information */
  moderation?: ContentModerationInfo;
}

export type CommunityChannelMessageLinkBase = {
  href: string;
  title?: string;
  description?: string;
};

export type CommunityChannelMessageVideoClipLink =
  CommunityChannelMessageLinkBase & {
    type: CommunityChannelMessageLinkType.VideoClip;
    id: string;
    thumbnailUrl?: string;
    videoUrl: string;
    processedVideoUrl?: string; // processed video url
    products?: PopClipProductInfo[];
    accessLevel?: PopStoreUserAccessLevel;
    showLockedVideoThumbnail?: boolean;
    tag?: MediaGalleryObjectTag;
    contentType?: SeriesElementContentType;
    relation?:
      | {
          type: 'series';
          seriesId: string;
          seriesName: string;
          labelFormat?: SeriesLabelFormat;
          showLabel?: boolean;
          hasChapter?: boolean;
          chapterLabelFormat?: SeriesChapterLabelFormat;
          mobileBannerImage?: string;
          desktopBannerImage?: string;
          elementSortingStrategy?: SeriesElementSortingStrategy;
          accessLevel?: PopStoreUserAccessLevel;
        }
      | {
          type: 'course';
          seriesId: string;
          seriesName: string;
          chapterId?: string;
          chapterName?: string;
          accessLevel?: PopStoreUserAccessLevel;
        };
  };

export type CommunityChannelMessageSeriesLink =
  CommunityChannelMessageLinkBase & {
    type: CommunityChannelMessageLinkType.Series;
    id: string;
    labelFormat?: SeriesLabelFormat;
    showLabel?: boolean;
    hasChapter?: boolean;
    chapterLabelFormat?: SeriesChapterLabelFormat;
    mobileBannerImage?: string;
    desktopBannerImage?: string;
    elementSortingStrategy?: SeriesElementSortingStrategy;
    accessLevel?: PopStoreUserAccessLevel;
  };

export type CommunityChannelMessagePostLink =
  CommunityChannelMessageLinkBase & {
    type: CommunityChannelMessageLinkType.Post;
    id: string;
    cid: string;
    postType?: PostContentType;
    visibility: PostVisibility;
    permalink: string;
    createdAt: string;
    publishedAt?: string;
    imageCount?: number;
    videoCount?: number;
    medias?: PostMedia[];
    payToView?: Post['payToView'];
    subscribeToView?: Post['subscribeToView'];
    tipToView?: Post['tipToView'];
    tippingAmounts?: Post['tippingAmounts'];
    crowdTippingToView?: Post['crowdTippingToView'];
    allowComments: boolean;
  };

export type CommunityChannelMessageCourseLink =
  CommunityChannelMessageLinkBase & {
    type: CommunityChannelMessageLinkType.Course;
    id: string;
    chapterId?: string;
    chapterName?: string;
    mobileBannerImage?: string;
    desktopBannerImage?: string;
    accessLevel?: PopStoreUserAccessLevel;
  };

export type CommunityChannelMessageProductLink =
  CommunityChannelMessageLinkBase & {
    type: CommunityChannelMessageLinkType.Product;
    listingType?: ListingType;
    id: string;
    defaultImage?: string;
    coverImage?: string;
    thumbnailImage?: string;
    price: number;
    discountPrice?: number;
    sku?: string;
    quantity: number;
    externalCheckoutUrl?: string;
    externalBrandName?: string;
    externalBrandImage?: string;
    // for meeting, this is topic of discussion
    summary?: string;
    discountEnabled?: boolean;
    discountStartDate?: string;
    discountEndDate?: string;
    meeting?: Listing['meeting'];
    derivedFrom?: Listing['derivedFrom'];
  };

export type CommunityChannelMessageUnknownLink =
  CommunityChannelMessageLinkBase & {
    type: CommunityChannelMessageLinkType.Unknown;
  };

export type CommunityChannelMessageLink =
  | CommunityChannelMessageVideoClipLink
  | CommunityChannelMessageSeriesLink
  | CommunityChannelMessagePostLink
  | CommunityChannelMessageCourseLink
  | CommunityChannelMessageProductLink
  | CommunityChannelMessageUnknownLink;

export enum CommunityChannelMessageTextContentType {
  PlainText = 'plain-text',
  Markdown = 'markdown',
  SlateJsDom = 'slatejs-dom',
}

export interface CommunityChannelMessageTextContent {
  type: CommunityChannelMessageTextContentType;
  content: string;
}

export interface CommunityChannelMessageContent {
  // text content in markdown format, information about links will be
  // saved in links field
  textContent: CommunityChannelMessageTextContent;
  attachments?: CommunityChannelMessageAttachment[];
  // either a list of links parsed from text content or attached by
  // user manually.
  links?: CommunityChannelMessageLink[];
}

export interface CommunityChannelMessageMention {
  // user id of the mention embed, e.g., [@username](#userid)
  userId: string;
  profile: BasicProfile;
}

export enum CommunityChannelMessagePredefinedReactionName {
  Like = 'like',
}

export interface BasicCommunityChannelMessage extends Pick<
  CommunityChannelMessage,
  'rootMessageId' | 'userId' | 'profile' | 'createdAt' | 'content'
> {
  // add more fields if needed

  messageId: string;
}

// /stores/{storeId}/communityChannels/{autoId}/messages/{autoId}
export interface CommunityChannelMessage extends ModelBase {
  // message id (ulid) generated on server side in lexicographic sorting
  // in order to support message sorting and pagination by cursor instead of
  // by traditional offset based approach.
  // NOTE: doc id is the same as messageId
  // messageId: string;

  // used to identify the message created by the client for optimistic update
  // uniqueness is not guaranteed, but should be unique in a client channel.
  clientMessageId: string;
  // e.g. 1,2,3 etc, only support 3 levels of messages
  level: number;
  rootMessageId: string;
  parentMessageId?: string;
  userId: string;
  profile: CommunityChannelMemberProfile;
  subscriptionLevel?: PopStoreExtendedSubscriptionLevel;
  content: CommunityChannelMessageContent;
  mentions?: CommunityChannelMessageMention[];
  deleted: boolean;
  // can be deleted by the user or the creator
  deletedByUserId?: string;
  deletedAt?: AppDate;
  lastRepliedAt?: AppDate;
  recentRepliedProfiles: { userId: string; profile: BasicProfile }[]; // up to 5 profiles for now
  reactions: Record<
    string /*reaction name*/,
    {
      emoji: string;
      emojiCode: string;
      count: number;
      usernames: string[] /* up to 5 usernames */;
    }
  >;
  replyCount: number;
  pinnedAt?: AppDate;
  moderation?: ContentModerationInfo;
}

// /stores/{storeId}/communityChannels/{autoId}/messages/{autoId}/reactions/{userId}-{reaction-name}
export interface CommunityChannelMessageReaction extends ModelBase {
  userId: string;
  profile: BasicProfile;
  emoji: string; // the emoji character, for example: '😀'.
  // The unified code of the emoji, for consistent emoji rendering
  // check https://github.com/ealush/emoji-picker-react for more information
  emojiCode: string;
  name?: CommunityChannelMessagePredefinedReactionName;
}

// /privateProfiles/{userId}/communityChannelBookmarks/{autoId}
export interface CommunityChannelMessageBookmark extends ModelBase {
  creatorId: string;
  creatorProfile: BasicProfile;
  channelId: string;
  channelName: string;
  messageId: string;
  message: CommunityChannelMessage;
}

// /stores/{storeId}/communityChannels/{autoId}/reports/{autoId}
export interface CommunityChannelMessageReport extends ModelBase {
  reporterUserId: string;
  reporterProfile: BasicProfile;
  reason: ReportReasonEnum;
  messageId: string;
  message: CommunityChannelMessage;
}

export type CommunityChannelLimitSettings = {
  [p in SaasSubscriptionBasePlanType]: {
    maxChannels: number;
    // maximum number of members allowed in a channel.
    // subscribe to, e.g., 'Creator Pro' plan can increase this limit
    // make the rules configurable but take the following defaults:
    // - beautify: 500
    // - expert: 2500
    // - community: 5000
    // - pro: unlimited
    // note: expert and community are at the same level.
    maxMembersPerChannel: number;
  };
};
